/**
 * /admin/bugs — operators: the bug reports users sent with the bug button
 * (components/BugButton.tsx; GET /ops/bug-reports, newest first, the last 200).
 * Each shows who sent it, when and from which page, the three answers, and
 * what the app added: the browser, the window, the web build, the API's
 * release and the last errors the tab saw.
 */
import { Link } from "react-router";
import { get } from "../api/client";
import { useLoad } from "../api/hooks";
import type { BugReport } from "../api/types";
import { useAuth } from "../auth/AuthContext";
import { Shell } from "../components/Shell";
import { ErrorLine } from "../components/ui";
import { plural } from "../lib/format";
import { ago } from "../lib/pipeline";
import { OperatorsOnly } from "./AdminPipeline";

export default function AdminBugs() {
  const { me } = useAuth();
  const list = useLoad(() => get<{ reports: BugReport[] }>("/ops/bug-reports"), []);
  if (me && !me.user.is_operator) return <OperatorsOnly shows="every user's bug reports" />;
  const reports = list.data?.reports ?? [];
  return (
    <Shell back="/" title="Bug reports">
      <div className="p" style={{ paddingTop: 24, paddingBottom: 48, maxWidth: 820 }} data-testid="admin-bugs">
        <p className="cap">Operators</p>
        <h1 className="mt6">Bug reports</h1>
        <p className="sub mt10">{list.data ? `${plural(reports.length, "report")}, newest first.` : "Loading…"}</p>
        <ErrorLine error={list.error} />
        {list.data && reports.length === 0 && <p className="tiny mt24">No reports yet.</p>}
        {reports.map((r) => <Report key={r.id} r={r} />)}
      </div>
    </Shell>
  );
}

function Report({ r }: { r: BugReport }) {
  const errors = r.context.errors ?? [];
  const videoId = /^\/videos\/(vid_[a-z0-9]+)/.exec(r.page)?.[1];
  const when = new Date(r.created_at).toLocaleString(undefined, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
  const vp = r.context.viewport;
  return (
    <article className="card bug mt16" data-testid="bug-report">
      <div className="tiny">
        <span title={r.created_at}>{when} · {ago(r.created_at)}</span> · {r.user.email} · <Link className="u" to={r.page}>{r.page}</Link>
        {videoId && <> · <Link className="u" to={`/admin/pipeline/${videoId}`}>its pipeline</Link></>}
      </div>
      <Answer q="What were you doing?" a={r.doing} />
      <Answer q="What did you see?" a={r.saw} />
      <Answer q="How can we reproduce it?" a={r.reproduce} />
      <details className="mt16">
        <summary className="tiny">Browser and app version{errors.length ? `, ${plural(errors.length, "error")} before it was sent` : ", no errors before it was sent"}</summary>
        <dl className="bug-ctx mt10">
          <div><dt>Browser</dt><dd>{r.user_agent ?? "unknown"}</dd></div>
          <div><dt>Window</dt><dd>{vp ? `${vp[0]} × ${vp[1]}${r.context.dpr ? ` at ${r.context.dpr}x` : ""}` : "unknown"}{r.context.language ? ` · ${r.context.language}` : ""}</dd></div>
          <div><dt>Web build</dt><dd>{r.web_build ?? "dev server"}{r.context.origin ? ` on ${r.context.origin}` : ""}</dd></div>
          <div><dt>API release</dt><dd>{r.release}</dd></div>
          {errors.map((e, i) => (
            <div key={i} data-testid="bug-error">
              <dt>{new Date(e.at).toLocaleTimeString()}</dt>
              <dd><b>{e.where}</b> {e.message}</dd>
            </div>
          ))}
        </dl>
      </details>
    </article>
  );
}

function Answer({ q, a }: { q: string; a: string }) {
  return (
    <>
      <h3 className="mt16">{q}</h3>
      <p className={`ans mt6${a ? "" : " tiny"}`}>{a || "Left blank."}</p>
    </>
  );
}
